import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WavyDivider } from '../components/WavyDivider';
import { BlogCard } from '../components/BlogCard';
import { blogPosts, categories, type ContentBlock, type BlogPost as BlogPostType } from '../data/blog';
import { useSeo } from '../lib/useSeo';
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
function ContentRenderer({
  block
}: {
  block: ContentBlock;
}) {
  switch (block.type) {
    case 'heading':
      return <h2 className="font-serif text-3xl md:text-4xl text-charcoal mt-12 mb-6" data-id="element-231">
          {block.text}
        </h2>;
    case 'paragraph':
      return <p className="text-charcoal-50 font-light leading-relaxed text-lg" data-id="element-232">
          {block.text}
        </p>;
    case 'list':
      return block.ordered ? <ol className="space-y-4 list-decimal pl-6 marker:text-sage-700 marker:font-serif" data-id="element-233">
          {block.items.map((item, i) => <li key={i} className="text-charcoal-50 font-light leading-relaxed text-lg pl-2" data-id="element-234">
              {item}
            </li>)}
        </ol> : <ul className="space-y-3" data-id="element-235">
          {block.items.map((item, i) => <li key={i} className="text-charcoal-50 font-light leading-relaxed text-lg flex gap-3" data-id="element-236">
              <span className="text-sage-700 select-none" data-id="element-237">–</span>
              <span data-id="element-238">{item}</span>
            </li>)}
        </ul>;
    case 'quote':
      return <div className="my-10 border-l-2 border-sage pl-6" data-id="element-239">
          <p className="font-serif italic text-2xl md:text-3xl text-charcoal leading-snug mb-3" data-id="element-240">
            {block.text}
          </p>
          {block.author && <p className="text-charcoal-50 text-sm tracking-widest uppercase" data-id="element-241">
              — {block.author}
            </p>}
        </div>;
    default:
      return null;
  }
}
export function BlogPost() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const post = blogPosts.find(p => p.slug === slug);
  if (!post) {
    return <Navigate to="/blog" replace data-id="element-242" />;
  }
  const category = categories.find(c => c.id === post.category)!;
  const Icon = category.icon;
  useSeo({
    title: post.title,
    description: post.excerpt,
    path: `/blog/${post.slug}`,
    image: post.image
  });
  // Related: prefer same category, fall back to latest others
  const sameCategory = blogPosts.filter(p => p.slug !== post.slug && p.category === post.category);
  const others = blogPosts.filter(p => p.slug !== post.slug && p.category !== post.category);
  const related: BlogPostType[] = [...sameCategory, ...others].slice(0, 3);
  return <div className="min-h-screen flex flex-col bg-cream" data-id="element-243">
      <Header data-id="element-244" />

      {/* Hero */}
      <section className="relative h-[65vh] min-h-[480px] flex items-center justify-center overflow-hidden" data-id="element-245">
        <div className="absolute inset-0 z-0" data-id="element-246">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" data-id="element-247" />
          <div className="absolute inset-0 bg-charcoal/40" data-id="element-248"></div>
        </div>
        <div className="relative z-10 text-center px-6 mt-16 max-w-4xl mx-auto" data-id="element-249">
          <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase font-medium text-white/90 mb-5 [text-shadow:0_1px_4px_rgba(0,0,0,0.3)]" data-id="element-250">
            <Icon size={14} strokeWidth={1.5} data-id="element-251" />
            {category.name}
          </span>
          <h1 className="text-4xl md:text-6xl font-serif text-white mb-4 drop-shadow-md leading-tight" data-id="element-252">
            {post.title}
          </h1>
          <p className="text-white/90 font-light tracking-wide drop-shadow-md" data-id="element-253">
            {formatDate(post.date)}
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none" data-id="element-254">
          <WavyDivider fill="#F8F2E4" data-id="element-255" />
        </div>
      </section>

      {/* Article body */}
      <article className="py-20 px-6 bg-cream" data-id="element-256">
        <div className="max-w-2xl mx-auto space-y-6" data-id="element-257">
          {post.content.map((block, i) => <ContentRenderer key={i} block={block} data-id="element-258" />)}

          <div className="pt-12 mt-8 border-t border-cream-200" data-id="element-259">
            <Link to="/blog" className="inline-flex items-center text-sage-700 hover:text-sage-600 transition-colors text-sm tracking-widest uppercase group" data-id="element-260">
              <ArrowLeft size={16} strokeWidth={1.5} className="mr-2 transform group-hover:-translate-x-1 transition-transform" data-id="element-261" />
              Back to all reflections
            </Link>
          </div>
        </div>
      </article>

      {/* Related posts */}
      {related.length > 0 && <section className="relative bg-rose-50 pt-32 pb-20 px-6 mt-12" data-id="element-262">
          <div className="absolute top-0 left-0 right-0 -translate-y-full pointer-events-none" data-id="element-263">
            <WavyDivider fill="#F5EBEB" position="top" data-id="element-264" />
          </div>

          <div className="max-w-7xl mx-auto" data-id="element-265">
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal text-center mb-14" data-id="element-266">
              Continue reading
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-267">
              {related.map(p => <BlogCard key={p.slug} post={p} data-id="element-268" />)}
            </div>
            <div className="text-center" data-id="element-269">
              <Link to="/blog" className="inline-flex items-center text-sage-700 hover:text-sage-600 transition-colors text-sm tracking-widest uppercase group" data-id="element-270">
                See all reflections
                <ArrowRight size={16} strokeWidth={1.5} className="ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-271" />
              </Link>
            </div>
          </div>
        </section>}

      {/* Divider into footer */}
      <div className="relative bg-rose-50 pointer-events-none" data-id="element-272">
        <WavyDivider fill="#3A3A38" data-id="element-273" />
      </div>

      <Footer data-id="element-274" />
    </div>;
}